import clsx from 'clsx';
import { ReactNode } from 'react';

export interface SwitchProps {
  checked: boolean;
  checkedIcon?: ReactNode;
  color: ColorVariant;
  disabled: boolean;
  icon?: ReactNode;
  size: SizeVariant;
  variant: 'contained' | 'overflow';
}

export const CustomSwitch = ({
  checked,
  checkedIcon,
  color,
  disabled,
  icon,
  size,
  variant,
}: SwitchProps) => {
  const switchVariant =
    icon !== undefined || checkedIcon !== undefined ? 'overflow' : variant;
  const classes = useStyles();

  return (
    <div className="flex relative">
      <span
        id="switch-thumb"
        className={clsx(
          classes.switch.thumb.default,
          classes.switch.thumb.variant[switchVariant][size],
          {
            [classes.switch.thumb.disabled]: disabled,
            'translate-x-4 ':
              switchVariant === 'contained' && checked && size !== 'small',
            'translate-x-6 ':
              switchVariant === 'overflow' && checked && size !== 'small',
            'translate-x-3 ': size === 'small' && checked,
            'translate-x-[20px] ':
              size === 'small' && switchVariant === 'overflow' && checked,
            'bg-white': checkedIcon === undefined || icon === undefined,
            'bg-[#232323] children:translate-x-[5px] children:translate-y-[2px] box-content':
              checkedIcon !== undefined || icon !== undefined,
          }
        )}
      >
        {checked ? checkedIcon : icon}
      </span>
      <span
        id="switch-track"
        className={clsx(
          classes.switch.track.default,
          classes.switch.track.variant[switchVariant][size],
          {
            [classes.switch.color[color]]: checked,
            [classes.switch.track.disabled]: disabled,
            [classes.switch.track.unchecked]: !checked,
          }
        )}
      />
    </div>
  );
};

const useStyles = () => ({
  switch: {
    color: {
      primary: 'bg-primary',
      secondary: 'bg-secondary',
    },
    track: {
      default: 'rounded-full ease-in-out transition duration-400 m-1',
      disabled: 'bg-neutral-800 dark:bg-neutral-500',
      unchecked: 'bg-neutral-400',
      variant: {
        contained: {
          small: 'w-8 h-4',
          medium: 'w-10 h-5',
          large: 'w-11 h-6',
        },
        overflow: {
          small: 'w-8 h-2',
          medium: 'w-10 h-3',
          large: 'w-11 h-4',
        },
      },
    },
    thumb: {
      default:
        'absolute top-[6px] left-2 rounded-full shadow ease-in-out duration-150',
      disabled: 'bg-neutral-100',
      variant: {
        contained: {
          small: 'w-3 h-3',
          medium: 'w-4 h-4',
          large: 'w-5 h-5',
        },
        overflow: {
          small: 'top-[-4px] left-[-2px] w-4 h-4',
          medium: 'top-[-2px] left-[-2px] w-6 h-6',
          large: 'top-[-4px] left-[-2px] w-6 h-6',
        },
      },
    },
  },
});
